import { useState, type ReactNode } from 'react';
import { Icon } from './ui.tsx';

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard API unavailable (http, old browser): fall back to a hidden textarea.
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  }
}

/** Just the copy button of a fix prompt, for places that show the prompt elsewhere (the latest result). */
export function CopyPromptButton({ prompt, label = 'Copy the prompt', className = 'button' }: { prompt: string; label?: string; className?: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copy = async () => {
    setState((await copyText(prompt)) ? 'copied' : 'failed');
    setTimeout(() => setState('idle'), 2500);
  };
  return (
    <button className={className} type="button" onClick={() => void copy()}>
      <Icon name="copy" />
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy blocked' : label}
    </button>
  );
}

/**
 * The fix band of the report: on the night ground, the three steps from report to fix on one side and the first
 * lines of the prompt on the other, the rest folded. `again` is the way to run the same visitors after fixing.
 */
export function FixBand({ prompt, title, lede, again, id = 'fix' }: { prompt: string; title: string; lede: string; again?: ReactNode; id?: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [open, setOpen] = useState(false);
  const copy = async () => {
    setState((await copyText(prompt)) ? 'copied' : 'failed');
    setTimeout(() => setState('idle'), 2500);
  };
  const changes = prompt.split('\n').filter((l) => /^\s*\d+[.)]\s/.test(l)).length;
  return (
    <section id={id} className="fix-night" aria-label="Fix prompt">
      <div className="r3-wrap fix-night-row">
        <div className="fix-night-text">
          <h2>{title}</h2>
          <p>{lede}</p>
          <ol className="fix-night-steps">
            <li>Copy the prompt</li>
            <li>Paste it into Lovable, Cursor or Bolt</li>
            <li>Run the same visitors again and compare</li>
          </ol>
          <div className="fix-night-actions">
            <button className="button night-primary" type="button" onClick={() => void copy()}>
              <Icon name="copy" />
              {state === 'copied' ? 'Copied' : 'Copy the prompt'}
            </button>
            {again}
          </div>
          {state === 'failed' && (
            <p className="form-error" role="alert">
              Your browser blocked copying. Open the prompt, select the text and copy it.
            </p>
          )}
        </div>
        <div className="fix-night-panel">
          <div className="fix-night-panel-head">
            <span>The prompt</span>
            {changes > 0 && <span>{changes} {changes === 1 ? 'change' : 'changes'}</span>}
          </div>
          <pre className={open ? 'fix-night-prompt open' : 'fix-night-prompt'} tabIndex={0}>
            {prompt}
          </pre>
          <button className="fix-night-toggle" type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? 'Fold it' : 'Show it all'} <Icon name="chevronDown" className={open ? 'flip' : undefined} />
          </button>
        </div>
      </div>
      <span className="sr-only" aria-live="polite">
        {state === 'copied' ? 'Prompt copied to the clipboard' : ''}
      </span>
    </section>
  );
}

/**
 * The prompt to paste into Lovable, Cursor or Bolt, with a copy button. `band` is the one action of the test
 * page: a tinted band with its own title, the copy button, and the text folded away.
 */
export function FixPrompt({ prompt, onCopy, band }: { prompt: string; onCopy?: () => void; band?: { title: string; lede: string } }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copy = async () => {
    const ok = await copyText(prompt);
    setState(ok ? 'copied' : 'failed');
    if (ok) onCopy?.();
    setTimeout(() => setState('idle'), 2500);
  };
  if (band) {
    return (
      <section className="fix-band" aria-label="Fix prompt">
        <div className="fix-band-head">
          <div>
            <h2>{band.title}</h2>
            <p>{band.lede}</p>
          </div>
          <button className="button cobalt big" type="button" onClick={() => void copy()}>
            {state === 'copied' ? 'Copied' : 'Copy the prompt'}
          </button>
        </div>
        {state === 'failed' && (
          <p className="form-error" role="alert">
            Your browser blocked copying. Open the prompt below, select the text and copy it.
          </p>
        )}
        <details className="fix-band-text">
          <summary>See the prompt</summary>
          <pre className="fix-prompt-text" tabIndex={0}>
            {prompt}
          </pre>
        </details>
        <span className="sr-only" aria-live="polite">
          {state === 'copied' ? 'Prompt copied to the clipboard' : ''}
        </span>
      </section>
    );
  }
  return (
    <section className="fix-prompt" aria-label="Fix prompt">
      <div className="fix-prompt-head">
        <div>
          <h2>Fix it with AI</h2>
          <p className="muted small">Paste this into Lovable, Cursor or Bolt. It lists the problems the visitors hit and what to change.</p>
        </div>
        <button className="button primary" type="button" onClick={() => void copy()}>
          {state === 'copied' ? 'Copied' : 'Copy prompt'}
        </button>
      </div>
      {state === 'failed' && (
        <p className="form-error" role="alert">
          Your browser blocked copying. Select the text below and copy it.
        </p>
      )}
      <pre className="fix-prompt-text" tabIndex={0}>
        {prompt}
      </pre>
      <span className="sr-only" aria-live="polite">
        {state === 'copied' ? 'Prompt copied to the clipboard' : ''}
      </span>
    </section>
  );
}
